import { api } from './api';
import type { PageChangeEvent, ChangeFilters } from '../types/changeDetection.types';

export const changeDetectionService = {
  /**
   * Get page changes by page ID with optional filters
   */
  getPageChanges: async (
    pageId: number,
    filters?: ChangeFilters
  ): Promise<PageChangeEvent[]> => {
    const params = new URLSearchParams();
    
    if (filters?.types) {
      filters.types.forEach(type => params.append('types', type));
    }
    if (filters?.minSeverity) {
      params.set('minSeverity', filters.minSeverity);
    }
    if (filters?.from) {
      params.set('from', filters.from);
    }
    if (filters?.to) {
      params.set('to', filters.to);
    }
    
    const response = await api.get(
      `/pages/${pageId}/changes?${params}`
    );
    const data = response.data;
    // Handle both paginated response {items: [...]} and direct array [...]
    if (Array.isArray(data)) {
      return data;
    }
    return data.items || [];
  },

  /**
   * Get page changes by page URL with optional filters
   * Alternative endpoint for customer-facing queries using page URL instead of ID
   */
  getPageChangesByUrl: async (
    pageUrl: string,
    filters?: ChangeFilters
  ): Promise<PageChangeEvent[]> => {
    const params = new URLSearchParams();
    params.set('pageUrl', pageUrl);
    
    if (filters?.types) {
      filters.types.forEach(type => params.append('types', type));
    }
    if (filters?.minSeverity) {
      params.set('minSeverity', filters.minSeverity);
    }
    if (filters?.from) {
      params.set('from', filters.from);
    }
    if (filters?.to) {
      params.set('to', filters.to);
    }
    
    const response = await api.get(
      `/pages/changes/by-url?${params}`
    );
    const data = response.data;
    // Handle both paginated response {items: [...]} and direct array [...]
    if (Array.isArray(data)) {
      return data;
    }
    return data.items || [];
  },

  /**
   * Get single change event details
   */
  getChangeEvent: async (eventId: number): Promise<PageChangeEvent> => {
    const response = await api.get(`/changes/${eventId}`);
    return response.data;
  },

  /**
   * Trigger recompute of changes for a page (admin only)
   */
  recomputeChanges: async (pageId: number): Promise<void> => {
    await api.post(`/pages/${pageId}/changes/recompute`);
  }
};
